// Pure eligibility check for the Read-Aloud (TTS) feature.
//
// Extracted from SlideElementAttachmentPassage so it can be reused in
// live-game surfaces where `useDesignerContext` (the previous source of
// the lesson subject) isn't available. Callers pass `subject` directly,
// which keeps this function free of React dependencies and trivially
// unit-testable.

import { NATIVE_ENGLISH_COUNTRIES } from '../../utils/internationalCountries';

// Countries where TTS is always enabled regardless of subject — the
// native-English / school-plan markets that originally shipped
// Read-Aloud. Sourced from `NATIVE_ENGLISH_COUNTRIES` (the single
// source of truth) so this list can't drift out of sync with the
// international-country check and accidentally disable TTS for a
// native-English teacher (e.g. when `country='UK'` was missing here).
// Both 'UK' and 'GB' are included via the shared list — the country
// picker surfaces 'UK' as a selectable value while some access-badge
// orgs come through as 'GB'.
export const TTS_ENABLED_COUNTRIES: ReadonlyArray<string> = NATIVE_ENGLISH_COUNTRIES;

// CURI-301: TTS also enables when the lesson subject is one of these,
// regardless of country — international teachers using Curipod's English
// content can use Read-Aloud. The downstream license/paywall gate still
// applies (Premium / Enterprise depending on tenant country).
export const TTS_ENABLED_SUBJECTS: ReadonlyArray<string> = ['english', 'literacy'];

export interface TTSEligibilityInput {
  /** Org country from the access badge. */
  country: string | undefined;
  /** Lesson / template subject (e.g. 'english', 'literacy', 'ela', 'math'). */
  subject: string | undefined;
  /**
   * Tri-state intl-school flag from `accessBadge.organization.isInternational`:
   *  - `true`     — teacher explicitly answered "yes, intl school". Lessons
   *                 with subject 'ela' also unlock TTS (they're routed
   *                 through the US content library and ARE teaching English
   *                 even though the subject normalises to 'ela' internally).
   *  - `false`    — teacher explicitly answered "no, local school". The
   *                 ela-path stays gated.
   *  - `undefined` / `null` — never asked. Same TTS behaviour as `false`,
   *                 but other consumers (e.g. Unleash analytics) keep the
   *                 distinction. The downstream check below uses `=== true`
   *                 so all three non-true values behave identically here.
   */
  isInternational: boolean | undefined | null;
}

/**
 * Returns true iff Read-Aloud should be available for the current
 * teacher + lesson combination.
 *
 * The three independent paths:
 *  1. Native-English country (US/GB/AU/CA/IE/NZ) — always on.
 *  2. Subject is one of TTS_ENABLED_SUBJECTS ('english' / 'literacy')
 *     — enables for international teachers teaching English even when
 *     their country isn't in the native-English list.
 *  3. Subject is 'ela' AND `isInternational === true` — covers the
 *     "treat-as-US-teacher" path where English subjects normalise to
 *     'ela' but the teacher should still get TTS. We check `=== true`
 *     explicitly (not truthy) so `null` / `undefined` (never asked) and
 *     `false` (explicitly local school) both reliably skip the gate.
 */
export function isTTSEnabledByCountryOrSubject({
  country,
  subject,
  isInternational,
}: TTSEligibilityInput): boolean {
  // Normalize to upper-case so a lowercase code (e.g. 'us' from a stored
  // value) still matches. Mirrors the case-insensitive treatment in
  // utils/internationalCountries.ts so the two helpers can never disagree
  // on the same input.
  const normalizedCountry = country ? country.toUpperCase() : undefined;
  if (normalizedCountry && TTS_ENABLED_COUNTRIES.includes(normalizedCountry)) return true;
  // Normalize subject the same way. `template.metadata.subject` can come
  // through with title-case ('English', 'Literacy') in some lesson docs,
  // and the canonical SubjectType values are lowercase, so we lower-case
  // both sides of the comparison instead of leaving callers responsible.
  const normalizedSubject =
    typeof subject === 'string' ? subject.toLowerCase() : undefined;
  if (normalizedSubject && TTS_ENABLED_SUBJECTS.includes(normalizedSubject)) {
    return true;
  }
  if (normalizedSubject === 'ela' && isInternational === true) return true;
  return false;
}
